import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Paper, 
  CircularProgress, 
  Stepper, 
  Step, 
  StepLabel,
  styled,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Card,
  CardContent,
  Alert,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Chip
} from '@mui/material';
import { 
  Fingerprint, 
  DevicesOther, 
  Check, 
  Warning, 
  Error as ErrorIcon, 
  ArrowBack,
  Computer,
  Smartphone,
  Memory,
  Battery90,
  Storage,
  Speed,
  SettingsEthernet,
  Wifi,
  BluetoothSearching,
  Camera,
  Mic,
  Close,
  CloudUpload,
  PhotoCamera,
  AddPhotoAlternate,
  Delete
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Nav from '../components/Nav';

// Styled components
const ScanContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f5f5',
  minHeight: '100vh',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(8)
}));

const AIModelCard = styled(Card)(({ theme, selected }) => ({
  borderRadius: 0,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  height: '100%',
  transition: 'transform 0.3s ease',
  border: selected ? '2px solid #007C3F' : '1px solid #e0e0e0',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
  }
}));

const TrialBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 12,
  right: 12,
  backgroundColor: '#FFE302',
  color: '#000',
  padding: '4px 8px',
  fontSize: '12px',
  fontWeight: 'bold',
  zIndex: 1
}));

const PremiumBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 12,
  right: 12,
  backgroundColor: '#007C3F',
  color: '#fff',
  padding: '4px 8px',
  fontSize: '12px',
  fontWeight: 'bold',
  zIndex: 1
}));

const ScanPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: 0,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
}));

const ScanButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#000',
  color: '#fff',
  padding: theme.spacing(1.5, 4),
  borderRadius: 0,
  '&:hover': {
    backgroundColor: '#333'
  }
}));

const DeviceCard = styled(Card)(({ theme }) => ({
  borderRadius: 0,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  height: '100%',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
  }
}));

const ScanProgressContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6, 0),
  textAlign: 'center'
}));

const AnimatedScanLine = styled(Box)(({ theme, active }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '2px',
  background: 'linear-gradient(90deg, transparent, #007C3F, transparent)',
  animation: active ? 'scanAnimation 2s infinite' : 'none',
  '@keyframes scanAnimation': {
    '0%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(300px)' },
    '100%': { transform: 'translateY(0)' }
  }
}));

const ResultItem = styled(ListItem)(({ theme, status }) => ({
  borderLeft: `4px solid ${
    status === 'good' ? '#4caf50' : 
    status === 'warning' ? '#ff9800' : 
    status === 'error' ? '#f44336' : '#e0e0e0'
  }`,
  marginBottom: theme.spacing(1),
  backgroundColor: '#fff'
}));

// Main component
const Scan = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  const [deviceType, setDeviceType] = useState(null);
  const [aiModel, setAiModel] = useState(null);
  const [scanResults, setScanResults] = useState([]);
  const [remainingTrials, setRemainingTrials] = useState(3);
  const [isPremium, setIsPremium] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  
  const steps = ['Select Device', 'Choose AI Model', 'Upload Images', 'Prepare Device', 'Scan Device', 'View Results'];
  
  // Simulated device detection
  useEffect(() => {
    if (scanning && activeStep === 2) {
      const timer = setInterval(() => {
        setProgress((prevProgress) => {
          const newProgress = prevProgress + 5;
          if (newProgress >= 100) {
            clearInterval(timer);
            setTimeout(() => {
              setScanComplete(true);
              setActiveStep(3);
              setScanning(false);
              // Generate mock scan results
              generateMockResults();
            }, 500);
            return 100;
          }
          return newProgress;
        });
      }, 200);
      
      return () => {
        clearInterval(timer);
      };
    }
  }, [scanning, activeStep]);
  
  const generateMockResults = () => {
    const results = [
      { 
        component: 'Processor', 
        status: 'good', 
        icon: <Memory />, 
        details: 'Operating at optimal performance (3.2 GHz)' 
      },
      { 
        component: 'Battery', 
        status: 'warning', 
        icon: <Battery90 />, 
        details: 'Battery health at 78%. Consider replacement in 3-6 months.' 
      },
      { 
        component: 'Storage', 
        status: 'good', 
        icon: <Storage />, 
        details: '234GB available of 512GB. No issues detected.' 
      },
      { 
        component: 'Memory', 
        status: 'good', 
        icon: <Memory />, 
        details: '8GB RAM functioning normally.' 
      },
      { 
        component: 'Network', 
        status: 'error', 
        icon: <SettingsEthernet />, 
        details: 'Network adapter showing intermittent connection issues.' 
      },
      { 
        component: 'Wi-Fi', 
        status: 'good', 
        icon: <Wifi />, 
        details: 'Wi-Fi module functioning correctly.' 
      },
      { 
        component: 'Bluetooth', 
        status: 'warning', 
        icon: <BluetoothSearching />, 
        details: 'Bluetooth connectivity inconsistent.' 
      },
      { 
        component: 'Camera', 
        status: 'good', 
        icon: <Camera />, 
        details: 'Camera functioning normally.' 
      },
      { 
        component: 'Microphone', 
        status: 'good', 
        icon: <Mic />, 
        details: 'Microphone sensitivity within normal range.' 
      }
    ];
    
    setScanResults(results);
  };
  
  const handleDeviceSelect = (type) => {
    setDeviceType(type);
    setActiveStep(1);
  };
  
  const handleModelSelect = (model) => {
    setAiModel(model);
    setActiveStep(2);
  };
  
  // State for image upload
  const [selectedImages, setSelectedImages] = useState([]);
  const fileInputRef = useRef(null);
  
  const handleImageUpload = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const newImages = Array.from(files).map(file => ({
        file,
        preview: URL.createObjectURL(file),
        name: file.name
      }));
      setSelectedImages(prev => [...prev, ...newImages]);
    }
  };
  
  const handleRemoveImage = (index) => {
    setSelectedImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };
  
  const handleContinueAfterUpload = () => {
    setActiveStep(3);
  };
  
  const handleStartScan = () => {
    if (!isPremium && remainingTrials <= 0) {
      setShowUpgradeDialog(true);
      return;
    }
    
    if (!isPremium) {
      setRemainingTrials(prev => prev - 1);
    }
    
    setActiveStep(4);
    setScanning(true);
    setProgress(0);
  };
  
  const handleUpgrade = () => {
    navigate('/premium');
  };
  
  const handleCloseUpgradeDialog = () => {
    setShowUpgradeDialog(false);
  };
  
  const handleBack = () => {
    if (activeStep === 0) {
      navigate('/');
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };
  
  const handleReset = () => {
    setActiveStep(0);
    setScanning(false);
    setScanComplete(false);
    setProgress(0);
    setDeviceType(null);
    setScanResults([]);
  };
  
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select your device type
            </Typography>
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <DeviceCard onClick={() => handleDeviceSelect('computer')}>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Computer sx={{ fontSize: 60, color: '#007C3F', mb: 2 }} />
                    <Typography variant="h6">Computer / Laptop</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Desktop computers, laptops, and workstations
                    </Typography>
                  </CardContent>
                </DeviceCard>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DeviceCard onClick={() => handleDeviceSelect('mobile')}>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Smartphone sx={{ fontSize: 60, color: '#007C3F', mb: 2 }} />
                    <Typography variant="h6">Mobile Device</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Smartphones, tablets, and other mobile devices
                    </Typography>
                  </CardContent>
                </DeviceCard>
              </Grid>
            </Grid>
          </Box>
        );
      
      case 1:
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Choose AI Model for Diagnostics
              </Typography>
              
              {!isPremium && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    Free Trials Remaining: {remainingTrials}/3
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={handleUpgrade}
                    sx={{ borderRadius: 0 }}
                  >
                    Upgrade
                  </Button>
                </Box>
              )}
            </Box>
            
            {!isPremium && (
              <Alert severity="info" sx={{ mb: 3 }}>
                You have {remainingTrials} free scans remaining. Upgrade to premium for unlimited scans and access to all AI models.
              </Alert>
            )}
            
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ position: 'relative' }}>
                  <AIModelCard 
                    selected={aiModel === 'chatgpt'}
                    onClick={() => handleModelSelect('chatgpt')}
                  >
                    <TrialBadge>Free Trial</TrialBadge>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <Box 
                        component="img"
                        src="https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg"
                        alt="ChatGPT"
                        sx={{ height: 60, mb: 2 }}
                      />
                      <Typography variant="h6">ChatGPT</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Standard diagnostic capabilities with good accuracy for common issues
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                          <Check sx={{ color: '#4caf50', mr: 1, fontSize: 18 }} />
                          Basic component analysis
                        </Typography>
                        <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                          <Check sx={{ color: '#4caf50', mr: 1, fontSize: 18 }} />
                          Performance diagnostics
                        </Typography>
                        <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>
                          <Close sx={{ color: '#f44336', mr: 1, fontSize: 18 }} />
                          Advanced hardware analysis
                        </Typography>
                      </Box>
                    </CardContent>
                  </AIModelCard>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ position: 'relative' }}>
                  <AIModelCard 
                    selected={aiModel === 'claude'}
                    onClick={() => handleModelSelect('claude')}
                  >
                    <PremiumBadge>Premium</PremiumBadge>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <Box 
                        component="img"
                        src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/10/Claude_logo.svg/1200px-Claude_logo.svg.png"
                        alt="ClaudesonNet"
                        sx={{ height: 60, mb: 2 }}
                      />
                      <Typography variant="h6">ClaudesonNet</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Advanced diagnostic capabilities with superior accuracy and detailed analysis
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                          <Check sx={{ color: '#4caf50', mr: 1, fontSize: 18 }} />
                          Comprehensive component analysis
                        </Typography>
                        <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                          <Check sx={{ color: '#4caf50', mr: 1, fontSize: 18 }} />
                          Advanced performance diagnostics
                        </Typography>
                        <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Check sx={{ color: '#4caf50', mr: 1, fontSize: 18 }} />
                          Predictive failure analysis
                        </Typography>
                      </Box>
                    </CardContent>
                  </AIModelCard>
                </Box>
              </Grid>
            </Grid>
          </Box>
        );
      
      case 2:
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Upload Images of Your {deviceType === 'computer' ? 'Computer' : 'Mobile Device'}
              </Typography>
              
              <Button
                variant="contained"
                component="label"
                startIcon={<AddPhotoAlternate />}
                sx={{ 
                  borderRadius: 0,
                  bgcolor: '#007C3F',
                  '&:hover': { bgcolor: '#006633' }
                }}
              >
                Add Images
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  ref={fileInputRef}
                />
              </Button>
            </Box>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Upload images of your device showing the issue or problem areas. This helps the {aiModel === 'chatgpt' ? 'ChatGPT' : 'ClaudesonNet'} AI provide more accurate diagnostics.
            </Alert>
            
            {selectedImages.length > 0 ? (
              <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {selectedImages.map((image, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Box
                        sx={{
                          position: 'relative',
                          height: 200,
                          border: '1px solid #e0e0e0',
                          borderRadius: 0,
                          overflow: 'hidden'
                        }}
                      >
                        <img
                          src={image.preview}
                          alt={`Uploaded ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <IconButton
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: '#fff',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                          }}
                          onClick={() => handleRemoveImage(index)}
                        >
                          <Delete />
                        </IconButton>
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: '#fff',
                            p: 1,
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {image.name}
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Box>
                    <Chip 
                      icon={<PhotoCamera />} 
                      label={`${selectedImages.length} image${selectedImages.length !== 1 ? 's' : ''} uploaded`} 
                      color="primary" 
                      variant="outlined" 
                    />
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleContinueAfterUpload}
                    sx={{ 
                      borderRadius: 0,
                      bgcolor: '#000',
                      '&:hover': { bgcolor: '#333' }
                    }}
                  >
                    Continue
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 0,
                  p: 5,
                  textAlign: 'center',
                  bgcolor: '#f9f9f9',
                  cursor: 'pointer',
                  '&:hover': { borderColor: '#007C3F' }
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <CloudUpload sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Drag & Drop Images Here
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  or click to browse files
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  (Optional) Upload up to 5 images to help with diagnostics
                </Typography>
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={handleContinueAfterUpload}
                    sx={{ borderRadius: 0 }}
                  >
                    Skip this step
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        );
      
      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Prepare your {deviceType === 'computer' ? 'computer' : 'mobile device'} for scanning
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Follow these steps to ensure an accurate scan of your device
            </Alert>
            
            <List>
              <ListItem>
                <ListItemIcon><Check /></ListItemIcon>
                <ListItemText 
                  primary="Ensure your device is powered on and fully operational"
                  secondary="The device should not be in sleep or hibernation mode"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Check /></ListItemIcon>
                <ListItemText 
                  primary="Close any resource-intensive applications"
                  secondary="This will provide more accurate performance readings"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Check /></ListItemIcon>
                <ListItemText 
                  primary="Connect to a stable power source"
                  secondary="Battery-powered devices should be connected to power for best results"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Check /></ListItemIcon>
                <ListItemText 
                  primary="Ensure a stable internet connection"
                  secondary="The scan requires internet connectivity to analyze device components"
                />
              </ListItem>
            </List>
            
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <ScanButton 
                onClick={handleStartScan}
                startIcon={<Fingerprint />}
                size="large"
              >
                Start Scan
              </ScanButton>
            </Box>
          </Box>
        );
      
      case 4:
        return (
          <ScanProgressContainer>
            <Box sx={{ position: 'relative', width: '100%', height: '300px', mb: 4, border: '1px solid #e0e0e0' }}>
              {deviceType === 'computer' ? (
                <Computer sx={{ fontSize: 120, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#007C3F', opacity: 0.7 }} />
              ) : (
                <Smartphone sx={{ fontSize: 120, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#007C3F', opacity: 0.7 }} />
              )}
              <Box sx={{ position: 'absolute', top: '10px', right: '10px', backgroundColor: '#f9f9f9', padding: '8px', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" fontWeight="bold">
                  AI Model: {aiModel === 'chatgpt' ? 'ChatGPT' : 'ClaudesonNet'}
                </Typography>
                {selectedImages.length > 0 && (
                  <Typography variant="body2" sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                    <PhotoCamera sx={{ fontSize: 16, mr: 0.5 }} />
                    {selectedImages.length} image{selectedImages.length !== 1 ? 's' : ''} uploaded
                  </Typography>
                )}
              </Box>
              <AnimatedScanLine active={scanning} />
            </Box>
            
            <CircularProgress variant="determinate" value={progress} size={80} thickness={4} sx={{ mb: 3 }} />
            
            <Typography variant="h6" gutterBottom>
              Scanning your device...
            </Typography>
            
            <Typography variant="body1" color="textSecondary">
              Please do not disconnect or turn off your device during the scan
            </Typography>
            
            <Typography variant="h4" sx={{ mt: 2, fontWeight: 'bold' }}>
              {progress}%
            </Typography>
          </ScanProgressContainer>
        );
      
      case 5:
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Scan Results
              </Typography>
              
              <Button 
                variant="outlined" 
                onClick={handleReset}
                sx={{ borderRadius: 0 }}
              >
                New Scan
              </Button>
            </Box>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Our {aiModel === 'chatgpt' ? 'ChatGPT' : 'ClaudesonNet'} AI has analyzed your {deviceType === 'computer' ? 'computer' : 'mobile device'}{selectedImages.length > 0 ? ' and the uploaded images' : ''} to identify the following issues:
            </Alert>
            
            <List>
              {scanResults.map((result, index) => (
                <ResultItem key={index} status={result.status}>
                  <ListItemIcon>
                    {result.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={result.component} 
                    secondary={result.details}
                  />
                  {result.status === 'good' && <Check sx={{ color: '#4caf50' }} />}
                  {result.status === 'warning' && <Warning sx={{ color: '#ff9800' }} />}
                  {result.status === 'error' && <ErrorIcon sx={{ color: '#f44336' }} />}
                </ResultItem>
              ))}
            </List>
            
            <Box sx={{ mt: 4, p: 3, bgcolor: '#f9f9f9', border: '1px solid #e0e0e0' }}>
              <Typography variant="h6" gutterBottom>
                Recommendations
              </Typography>
              
              <Typography variant="body1" paragraph>
                Based on the scan results, we recommend the following actions:
              </Typography>
              
              <List>
                {scanResults.filter(r => r.status === 'error').length > 0 && (
                  <ListItem>
                    <ListItemIcon><ErrorIcon color="error" /></ListItemIcon>
                    <ListItemText 
                      primary="Critical issues require immediate attention" 
                      secondary="Please address the components marked in red as soon as possible"
                    />
                  </ListItem>
                )}
                
                {scanResults.filter(r => r.status === 'warning').length > 0 && (
                  <ListItem>
                    <ListItemIcon><Warning color="warning" /></ListItemIcon>
                    <ListItemText 
                      primary="Warning issues should be monitored" 
                      secondary="Components marked in yellow may need attention in the near future"
                    />
                  </ListItem>
                )}
                
                <ListItem>
                  <ListItemIcon><Check color="success" /></ListItemIcon>
                  <ListItemText 
                    primary="Schedule regular maintenance" 
                    secondary="We recommend scanning your device every 3 months for optimal performance"
                  />
                </ListItem>
              </List>
              
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button 
                  variant="contained" 
                  sx={{ 
                    bgcolor: '#007C3F',
                    '&:hover': { bgcolor: '#006633' },
                    borderRadius: 0
                  }}
                >
                  View Detailed Report
                </Button>
                
                <Button 
                  variant="outlined"
                  sx={{ borderRadius: 0 }}
                >
                  Email Results
                </Button>
              </Box>
            </Box>
          </Box>
        );
      
      default:
        return null;
    }
  };
  
  return (
    <Box>
      <Nav />
      <ScanContainer>
        <Container maxWidth="lg">
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={handleBack} sx={{ mr: 1 }}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h4" component="h1">
                Device Scanner
              </Typography>
            </Box>
            
            {!isPremium && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  Free Trials: {remainingTrials}/3
                </Typography>
                <Button 
                  variant="contained" 
                  size="small"
                  onClick={handleUpgrade}
                  sx={{ 
                    borderRadius: 0,
                    bgcolor: '#007C3F',
                    '&:hover': { bgcolor: '#006633' }
                  }}
                >
                  Upgrade to Premium
                </Button>
              </Box>
            )}
          </Box>
          
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          <ScanPaper>
            {renderStepContent()}
          </ScanPaper>
        </Container>
      </ScanContainer>
      
      {/* Upgrade Dialog */}
      <Dialog
        open={showUpgradeDialog}
        onClose={handleCloseUpgradeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
            You've used all your free trials
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ mb: 3 }}>
            Upgrade to Premium to get unlimited scans and access to advanced AI models like ClaudesonNet.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '400px', mx: 'auto', mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: '#f9f9f9', border: '1px solid #e0e0e0' }}>
              <Typography>Unlimited Device Scans</Typography>
              <Check sx={{ color: '#4caf50' }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: '#f9f9f9', border: '1px solid #e0e0e0' }}>
              <Typography>Access to ClaudesonNet AI</Typography>
              <Check sx={{ color: '#4caf50' }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: '#f9f9f9', border: '1px solid #e0e0e0' }}>
              <Typography>Detailed Diagnostic Reports</Typography>
              <Check sx={{ color: '#4caf50' }} />
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleCloseUpgradeDialog}
              sx={{ borderRadius: 0 }}
            >
              Maybe Later
            </Button>
            <Button
              variant="contained"
              onClick={handleUpgrade}
              sx={{ 
                borderRadius: 0,
                bgcolor: '#007C3F',
                '&:hover': { bgcolor: '#006633' }
              }}
            >
              Upgrade Now
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Scan;